import { useEffect, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import BillComponent from "./BillComponent";
import CashBookPrint from "./CashBookPrint";
import useDetectPrint from "../../hooks/useDetectPrint";
import { isMealBillAndroid, androidPrinter } from "../../native/mealbill-android.mjs";
import price from "../../helpers/calculatePrice";
import roundOffTotal from "../../helpers/roundOffTotal";
import moment from "moment";

/**
 * Build a flat ESC/POS receipt matching the PrintComponent/BillComponent.js layout exactly:
 * Header (restaurant, address, FSSAI) → customer name → branchOrderNumber + date
 * → orderType + orderNumber + table + payment → Item/Qty/Price/Amt table
 * → Sub Total, Discount, GST, Table Price, Charges, Round Off, Grand Total
 * → GST number (Tax Summary) → receipt message
 *
 * jobId format: "bill:<branchOrderNumber>" — stable per billing event.
 */
function createAndroidBillReceipt({ printData, restaurant, branchAddress, gstNumber, fssaiNumber, receiptMessage, tableType, printSetting, W }) {
  const branchOrderNumber = (printData?.branchOrderNumber ?? "").replace(/\s/g, "");
  const jobId = `bill:${branchOrderNumber}`.replace(/[^A-Za-z0-9._:-]/g, "-").slice(0, 128);

  const isExclusiveGST = printSetting?.exclusiveGST ?? false;
  const roundOffType = printSetting?.lowerRoundOff
    ? "lowerRoundOff"
    : printSetting?.upperRoundOff
      ? "upperRoundOff"
      : "standardRoundOff";

  // Calculate totals (same as ProductTable in BillComponent)
  const subTotal = parseFloat(printData?.itemsTotal || 0);
  const discount = parseFloat(printData?.discount || 0);
  const otherCharges = parseFloat(printData?.otherCharges || 0);
  const tablePrice = parseFloat(printData?.tablePrice || 0);
  const gstCharges = isExclusiveGST
    ? parseFloat(printData?.cgstCharges || 0) + parseFloat(printData?.sgstCharges || 0)
    : 0;
  const unrounded = subTotal - discount + otherCharges + tablePrice + gstCharges;
  const grandTotal = roundOffTotal(unrounded, roundOffType);
  const roundOff = grandTotal - unrounded;

  const lines = [];

  const SEP = "-".repeat(W);
  const padR = (s, n) => { const str = String(s ?? ""); return str.length >= n ? str.slice(0, n) : str + " ".repeat(n - str.length); };
  const padL = (s, n) => { const str = String(s ?? ""); return str.length >= n ? str : " ".repeat(n - str.length) + str; };
  const spreadEven = (...parts) => {
    const p = parts.filter(Boolean);
    if (!p.length) return "";
    if (p.length === 1) return p[0];
    const tl = p.reduce((s, x) => s + x.length, 0);
    const sp = W - tl;
    const gaps = p.length - 1;
    const spc = sp > 0 ? Math.floor(sp / gaps) : 2;
    const extra = sp > 0 ? sp - spc * gaps : 0;
    return p.map((x, i) => i < p.length - 1 ? x + " ".repeat(spc + (i < extra ? 1 : 0)) : x).join("");
  };
  // Item column widths scale with paper: 58mm → 15+4+6+7=32, 80mm → 20+5+8+9=42
  const colName  = W === 42 ? 20 : 15;
  const colQty   = W === 42 ?  5 :  4;
  const colPrice = W === 42 ?  8 :  6;
  const colAmt   = W === 42 ?  9 :  7;

  // ── RESET: force printer to left-align before every job ───────────────────
  // Prevents restaurant name from drifting left on 2nd/3rd prints
  // because the printer retains center-align from the last separator.
  lines.push({ text: "", align: "left" });

  // ── Header: restaurant | address | FSSAI, then blank line (no ---)  ───────
  if (restaurant) lines.push({ text: restaurant, align: "center", bold: true });
  if (branchAddress) lines.push({ text: branchAddress, align: "center" });
  if (fssaiNumber) lines.push({ text: `FSSAI Number ${fssaiNumber}`, align: "center" });
  lines.push({ text: "", align: "left" }); // blank line — explicit left to break out of center

  // ── Info rows ─────────────────────────────────────────────────────────────
  const { branchOrderNumber: bon, orderNumber, customerName, customerMobile, orderType, tableNumber, updatedAt } = printData;

  // Customer Name (if enableCustomer) — explicit left so it doesn't inherit center
  if (printSetting?.enableCustomer && customerName) {
    lines.push({ text: `Name : ${customerName}${customerMobile ? ` (${customerMobile})` : ""}`, align: "left" });
  }

  // branchOrderNumber (left)  |  date (right)
  const dateStr = updatedAt ? moment(updatedAt).format("DD/MM/YYYY HH:mm") : moment().format("DD/MM/YYYY HH:mm");
  lines.push({ columns: [bon ?? "", dateStr] });

  const metaLine = [orderType, orderNumber ? String(orderNumber) : "", tableNumber ? String(tableNumber) : ""].filter(Boolean);
  if (metaLine.length > 0) {
    if (metaLine.length === 1) {
      lines.push({ text: metaLine[0] });
    } else if (metaLine.length === 2) {
      lines.push({ columns: [metaLine[0], metaLine[1]] });
    } else {
      lines.push({ columns: [metaLine[0], `${metaLine[1]}    ${metaLine[2]}`] });
    }
  }

  // Separator before item table
  lines.push({ text: SEP, align: "center" });

  // 4-column item header using native bridge columns for left/right split
  lines.push({ columns: ["Item", `${padL("Qty", colQty)}${padL("Price", colPrice)}${padL("Amt", colAmt)}`], bold: true });
  lines.push({ text: SEP, align: "center" });

  // Item rows (native columns left/right split)
  (printData?.orderItems ?? [])
    .filter((item) => item.quantity > 0)
    .forEach((item) => {
      const unitPrice = price(tableType?.tableType, item) || item.itemPrice || 0;
      const lineTotal = (item.quantity * unitPrice).toFixed(2);
      lines.push({ columns: [item.itemName ?? "", `${padL(item.quantity, colQty)}${padL(unitPrice, colPrice)}${padL(lineTotal, colAmt)}`] });
    });

  // ── Totals — right-aligned (left side empty, matches image) ──────────────
  const showSubTotal = discount !== 0 || gstCharges !== 0 || otherCharges !== 0 || tablePrice !== 0;
  if (showSubTotal) {
    lines.push({ columns: ["", `Sub Total  \u20B9 ${subTotal.toFixed(2)}`] });
  }
  if (discount !== 0) {
    lines.push({ columns: ["", `Discount(-)  \u20B9 ${discount.toFixed(2)}`] });
  }
  if (isExclusiveGST && gstCharges !== 0) {
    lines.push({ columns: ["", `GST(+)  \u20B9 ${gstCharges.toFixed(2)}`] });
  }
  if (tablePrice !== 0) {
    lines.push({ columns: ["", `Table Price(+)  \u20B9 ${tablePrice.toFixed(2)}`] });
  }
  if (otherCharges !== 0) {
    lines.push({ columns: ["", `Charges(+)  \u20B9 ${otherCharges.toFixed(2)}`] });
  }
  const roundOffDisplay = parseFloat(roundOff.toFixed(2));
  if (roundOffDisplay !== 0) {
    lines.push({ columns: ["", `Round off  ${roundOffDisplay > 0 ? "+" : ""}${roundOffDisplay.toFixed(2)}`] });
  }
  // Grand Total — always, right-aligned, bold
  lines.push({ columns: ["", `Grand Total  \u20B9 ${grandTotal.toFixed(2)}`], bold: true });

  // Tax Summary
  if (gstNumber) {
    lines.push({ text: SEP, align: "center" });
    lines.push({ text: "" });
    lines.push({ text: "Tax Summary", align: "center", bold: true });
    lines.push({ text: `GST ${gstNumber}`, align: "center" });
    lines.push({ text: "" });

    // Compute taxable amount and GST rates for the summary table
    const cgstAmt = parseFloat(printData?.cgstCharges || 0);
    const sgstAmt = parseFloat(printData?.sgstCharges || 0);
    const taxableAmt = subTotal - discount; // taxable base
    const cgstPct = taxableAmt > 0 ? ((cgstAmt / taxableAmt) * 100).toFixed(1) : "0";
    const sgstPct = taxableAmt > 0 ? ((sgstAmt / taxableAmt) * 100).toFixed(1) : "0";

    // Header row: Taxable Amt | CGST@X% | SGST@X%
    // Bridge supports 2 cols — combine CGST+SGST on right
    lines.push({ columns: ["Taxable Amt", `CGST@${cgstPct}%  SGST@${sgstPct}%`], bold: true });
    lines.push({ text: SEP, align: "center" });
    lines.push({ columns: [taxableAmt.toFixed(2), `${cgstAmt.toFixed(2)}         ${sgstAmt.toFixed(2)}`] });
  }

  // ── Receipt message footer — bold, center (matches image) ─────────────────
  if (receiptMessage) {
    lines.push({ text: "", align: "left" });
    lines.push({ text: receiptMessage, align: "center", bold: true });
  }

  // ── RESET at end: leave printer in left-align for the next job ────────────
  lines.push({ text: "", align: "left" });

  return { jobId, printer: "bill", lines };
}


const PrintComponent = ({
  headers,
  footers,
  printData,
  printType,
  restaurant,
  logo,
  gstNumber,
  fssaiNumber,
  branchAddress,
  containerStyle,
  receiptMessage,
  printSetting,
  isRestaurantAdmin,
  tableType,
}) => {
  const isPrinting = useDetectPrint();

  const componentRef = useRef();
  const lastPrintData = useRef(null);
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    onAfterPrint: () => {
      // alert("print completed");
    },
  });

  useEffect(() => {
    if (isRestaurantAdmin) {
      handlePrint();
      return;
    }

    if (isPrinting || !printData) return;

    // ── Android native thermal print path ──────────────────────────────────
    if (isMealBillAndroid() && printType !== "cashbook") {
      try {
        if (lastPrintData.current === printData) return; // Deduplicate Strict Mode double renders
        lastPrintData.current = printData;

        androidPrinter.getProfiles().then(result => {
          const profile = result.profiles?.bill;
          const W = (profile?.width === 576 || profile?.width === 832) ? 42 : 32;

          const receipt = createAndroidBillReceipt({ printData, restaurant, branchAddress, gstNumber, fssaiNumber, receiptMessage, tableType, printSetting, W });
          receipt.jobId = `${receipt.jobId}:${Date.now()}`;

          return androidPrinter.print(receipt).then(
            (result) => console.log("Android bill print:", result.status, receipt.jobId),
            (error) => {
              console.error("Android bill print failed:", error.message, error.result);
              window.alert(`Print failed: ${error.message}`);
            }
          );
        }).catch(error => {
          console.error("Android bill receipt build error:", error.message);
          window.alert(`Print error: ${error.message}`);
        });
      } catch (error) {
        console.error("Android bill receipt build error:", error.message);
        window.alert(`Print error: ${error.message}`);
      }
      return; // Never fall back to browser print after native attempt
    }

    // ── Electron desktop path ───────────────────────────────────────────────
    if (window?.api?.isElectron) {
      console.log("isElectron inside", window.api);
      window.api.send(printData);
    } else {
      // ── Browser fallback (print dialog) ──────────────────────────────────
      handlePrint();
    }
  }, [printData, isPrinting]);

  const renderComponent = () => {
    console.log(
      "cash book",
      printData,
      restaurant,
      logo,
      gstNumber,
      receiptMessage,
      branchAddress,
      printSetting,
    );
    if (printType === "cashbook") {
      return (
        <CashBookPrint
          ref={componentRef}
          printData={printData}
          restaurant={restaurant}
          logo={logo}
          gstNumber={gstNumber}
          fssaiNumber={fssaiNumber}
          receiptMessage={receiptMessage}
          branchAddress={branchAddress}
          printSetting={printSetting}
        />
      );
    }
    return (
      <BillComponent
        ref={componentRef}
        orderData={printData}
        restaurant={restaurant}
        logo={logo}
        gstNumber={gstNumber}
        fssaiNumber={fssaiNumber}
        receiptMessage={receiptMessage}
        branchAddress={branchAddress}
        printSetting={printSetting}
        tableType={tableType}
      />
    );
  };
  return (
    <div style={{ ...containerStyle, width: "100%" }}>
      <div style={{ display: "none" }}>{renderComponent()}</div>
    </div>
  );
};
export default PrintComponent;
